import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
  Typography,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { APIHOST } from '../../common';
import {useApp} from "../AppContext"

interface EditTransportationProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  onUpdate: () => void;
}

export default function EditTransportation({ open, onClose, orderId, onUpdate }: EditTransportationProps) {
  const [transportationData, setTransportationData] = useState({
    requiresBusParking: false,
    requiresTransitToVenue: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(false);
  const { setSnackbarInput } = useApp();
  useEffect(() => {
    if (open && orderId) {
      fetchOrderTransportation();
    }
  }, [open, orderId]);

  const fetchOrderTransportation = async () => {
    try {
      setInitialLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order details');
      }

      const data = await response.json();
      setTransportationData({
        requiresBusParking: data.data.requiresBusParking || false,
        requiresTransitToVenue: data.data.requiresTransitToVenue || false
      });

    } catch (err) {
      setSnackbarInput({ message: err instanceof Error ? err.message : 'Failed to load order details', severity: 'error' });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            requiresBusParking: transportationData.requiresBusParking,
            requiresTransitToVenue: transportationData.requiresTransitToVenue
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update transportation settings');
      }

      // Close dialog and refresh
      handleClose();
      onUpdate();
      setSnackbarInput({ message: 'Transportation settings updated successfully', severity: 'success' });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update transportation settings');
      setSnackbarInput({ message: err instanceof Error ? err.message : 'Failed to update transportation settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setTransportationData({
      requiresBusParking: false,
      requiresTransitToVenue: false
    });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        <Typography variant="h6">Edit Transportation Settings</Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure transportation requirements for this order.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={transportationData.requiresBusParking}
                  onChange={(e) => setTransportationData(prev => ({ 
                    ...prev, 
                    requiresBusParking: e.target.checked 
                  }))}
                />
              }
              label="Requires Bus Parking"
              sx={{ mb: 2, display: 'block' }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={transportationData.requiresTransitToVenue}
                  onChange={(e) => setTransportationData(prev => ({ 
                    ...prev, 
                    requiresTransitToVenue: e.target.checked 
                  }))}
                />
              }
              label="Requires Transit to Venue"
              sx={{ display: 'block' }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || initialLoading}
        >
          {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}